import axios from "axios";
import { FirebaseError } from "firebase/app";
import { describeAxiosError, trackDiagnostic } from "../../utils/diagnostics";

export const LOGIN_FAILURE_CODES = [
  "LOGIN_USER_DISABLED",
  "LOGIN_USER_NOT_FOUND",
  "LOGIN_TOKEN_REVOKED",
  "LOGIN_COMPANY_SUSPENDED",
  "LOGIN_USER_INACTIVE",
  "LOGIN_USER_DELETED",
  "LOGIN_WRONG_PASSWORD",
  "LOGIN_TOO_MANY_ATTEMPTS",
  "LOGIN_NETWORK",
  "LOGIN_FAILED",
] as const;

export type LoginFailureCode = (typeof LOGIN_FAILURE_CODES)[number];

const WRONG_PASSWORD_CODES = new Set([
  "auth/wrong-password",
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
  "auth/invalid-email",
]);

export const classifyLoginFailure = (error: unknown): LoginFailureCode => {
  const firebaseCode = error instanceof FirebaseError ? error.code : undefined;
  const details = describeAxiosError(error);
  const messageKey = details.message_key;
  const status = details.http_status;
  const text = String(details.backend_message ?? "").toLowerCase();

  if (messageKey === "USER_DISABLED" || firebaseCode === "auth/user-disabled") {
    return "LOGIN_USER_DISABLED";
  }
  if (messageKey === "USER_NOT_FOUND" || firebaseCode === "auth/user-not-found") {
    return "LOGIN_USER_NOT_FOUND";
  }
  if (messageKey === "ID_TOKEN_REVOKED") return "LOGIN_TOKEN_REVOKED";
  if (
    messageKey === "COMPANY_SUSPENDED" ||
    (status === 403 && text.includes("suspend"))
  ) {
    return "LOGIN_COMPANY_SUSPENDED";
  }
  if (messageKey === "USER_INACTIVE" || text.includes("inactiv")) {
    return "LOGIN_USER_INACTIVE";
  }
  if (
    messageKey === "USER_DELETED" ||
    text.includes("eliminado") ||
    text.includes("deleted")
  ) {
    return "LOGIN_USER_DELETED";
  }
  if (firebaseCode && WRONG_PASSWORD_CODES.has(firebaseCode)) {
    return "LOGIN_WRONG_PASSWORD";
  }
  if (firebaseCode === "auth/too-many-requests") return "LOGIN_TOO_MANY_ATTEMPTS";
  if (
    firebaseCode === "auth/network-request-failed" ||
    (axios.isAxiosError(error) && !error.response)
  ) {
    return "LOGIN_NETWORK";
  }
  return "LOGIN_FAILED";
};

export const trackLoginFailure = (
  error: unknown,
  identifier: string,
  code: LoginFailureCode = classifyLoginFailure(error),
) => {
  trackDiagnostic("login_failed", {
    code,
    login_identifier: identifier,
    identifier_is_email: identifier.includes("@"),
    firebase_code: error instanceof FirebaseError ? error.code : undefined,
    ...describeAxiosError(error),
  });
};
